import { EventEmitter } from "@angular/core";
import { DropdownMenuData, DropdownMenuItem } from "@vg-constellation/angular-17/dropdown";

import { BeneficiaryType } from "../../types/BeneficiaryType";
import { Beneficiary, DropdownStateChangeEvent, getTrustLabel } from "@models/beneficiary.model";
import { Trust } from "@models/estateProfile.model";
import * as content from "@services/content/guided-flow/account-details-content";

export interface BeneficiaryCardTypeConfig {
  dropdownLabel: string;
  buildDropdownData: (ctx: BeneficiaryCardContext) => DropdownMenuData;
  getActiveId: (ctx: BeneficiaryCardContext) => string;
  handleChange: (event: DropdownStateChangeEvent, ctx: BeneficiaryCardContext) => void;
}

export interface BeneficiaryCardContext {
  beneficiary: Beneficiary;
  householdMembers: DropdownMenuItem[];
  trusts: Trust[];
  displayName: string;
  personChange: EventEmitter<{ beneficiary: Beneficiary; memberId: string }>;
  trustChange: EventEmitter<{ beneficiary: Beneficiary; memberId: string }>;
}

// To add a new beneficiary type (e.g., BUSINESS, CHARITY):
// 1. Add value to BeneficiaryType enum
// 2. Add entry to BENEFICIARY_CARD_TYPE_CONFIGS below
// 3. Add corresponding @Output() emitter in BeneficiaryCardComponent
// 4. Add content constants for dropdown label
export const BENEFICIARY_CARD_TYPE_CONFIGS: Record<BeneficiaryType, BeneficiaryCardTypeConfig> = {
  [BeneficiaryType.INDIVIDUAL_BY_NAME]: {
    dropdownLabel: content.BENEFICIARY_DROPDOWN_LABEL,

    buildDropdownData(ctx: BeneficiaryCardContext): DropdownMenuData {
      const currentPersonItem: DropdownMenuItem = {
        labelText: ctx.displayName,
        id: ctx.beneficiary.householdMemberId || "current-person",
      };
      const otherMembers = ctx.householdMembers.filter(
        (m) => m.id !== ctx.beneficiary.householdMemberId,
      );
      return { groups: [{ items: [currentPersonItem, ...otherMembers] }] };
    },

    getActiveId(ctx: BeneficiaryCardContext): string {
      return ctx.beneficiary.householdMemberId || "current-person";
    },

    handleChange(event: DropdownStateChangeEvent, ctx: BeneficiaryCardContext): void {
      const selectedId = event?.activeItems?.[0];
      if (selectedId && selectedId !== ctx.beneficiary.householdMemberId) {
        ctx.personChange.emit({ beneficiary: ctx.beneficiary, memberId: selectedId });
      }
    },
  },

  [BeneficiaryType.TRUST]: {
    dropdownLabel: content.BENEFICIARY_TRUST_DROPDOWN_LABEL,

    buildDropdownData(ctx: BeneficiaryCardContext): DropdownMenuData {
      const trustItems: DropdownMenuItem[] = ctx.trusts.map((trust) => ({
        labelText: getTrustLabel(trust),
        id: trust.householdMemberId || "",
      }));
      return { groups: [{ items: trustItems }] };
    },

    getActiveId(ctx: BeneficiaryCardContext): string {
      return ctx.beneficiary.householdMemberId || "";
    },

    handleChange(event: DropdownStateChangeEvent, ctx: BeneficiaryCardContext): void {
      const selectedId = event?.activeItems?.[0];
      if (selectedId && selectedId !== ctx.beneficiary.householdMemberId) {
        ctx.trustChange.emit({ beneficiary: ctx.beneficiary, memberId: selectedId });
      }
    },
  },
};
